import React from 'react';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Typography,
  TableCell,
  TableRow,
  TextField,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import EnhancedTable from './EnhancedTable';
import {Auth} from '../../Api/auth';
import Snackbar from '../CommonComponents/Snackbar';

import { useMsal } from "@azure/msal-react";

const PREFIX = 'CodesetsList';

const classes = {
  select: `${PREFIX}-select`,
  appBarHeader: `${PREFIX}-appBarHeader`,
  backdrop: `${PREFIX}-backdrop`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.select}`]: {
    width: '25%',
  },

  [`& .${classes.appBarHeader}`]: {
    color: theme.palette.text.primary,
    background: 'white',
    position: 'static',
    top: 0,
    left: 'auto',
    width: '100%',
    padding: theme.spacing(2, 1, 1),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }
}));

export default function CodesetsList() {

  const {instance}=useMsal();

  const [codesetData, setCodesetData] = React.useState({
    codeset: null,
    rows: [],
    headCells: [],
  });
  let codesets = {
    "languageCodeset": "Language",
    "requestStatusCodeset": "Request status",
    "roleCodeset": "Role",
    "userProjectActionCodeset": "User project action",
    "reasonCodeset": "Reason",
    "reasonCodesetName": "Reason codeset name",
  }
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    severity: "success",
    message: ""
  });
  const [backdropOpen, setBackdropOpen] = React.useState(false);

  const getRowsPromiseForCodeset=(url)=>{
    return Auth.authorizedFetch("/api/"+url,{},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      
      return response.json();
    });
  }

  const getRowsPromise=(value)=>{
    switch(value){
      case "languageCodeset":
        return getRowsPromiseForCodeset("getLanguages");
      case "requestStatusCodeset":
        return getRowsPromiseForCodeset("getRequestStatuses");
      case "roleCodeset":
        return getRowsPromiseForCodeset("getUserRoles");
      case "userProjectActionCodeset":
        return getRowsPromiseForCodeset("getUserProjectActions");
      case "reasonCodeset":
        return getRowsPromiseForCodeset("getReasons");
      case "reasonCodesetName":
        return getRowsPromiseForCodeset("getReasonCodesetNames");
      default:
        return [];
    }
  }

  const getHeadCells=(value)=>{
    switch(value){
      case "languageCodeset":
        return [
          {id: 'id', label: "Id", isOrderable: true},
          {id: 'code', label: "Code", isOrderable: true}
        ];
      case "requestStatusCodeset":
        return [
          {id: 'id', label: "Id", isOrderable: true},
          {id: 'name_en', label: "Name(en)", isOrderable: true},
          {id: 'name_fr', label: "Name(fr)", isOrderable: true}
        ];
      case "roleCodeset":
        return [
          {id: 'id', label: "Id", isOrderable: true},
          {id: 'name', label: "Name", isOrderable: true}
        ];
      case "userProjectActionCodeset":
        return [
          {id: 'id', label: "Id", isOrderable: true},
          {id: 'name_en', label: "Name(en)", isOrderable: true},
          {id: 'name_fr', label: "Name(fr)", isOrderable: true}
        ];
      case "reasonCodeset":
        return [
          {id: 'id', label: "Id", isOrderable: true},
          {id: 'codeset_id', label: "Codeset id", isOrderable: true},
          {id: 'name_en', label: "Name(en)", isOrderable: true},
          {id: 'name_fr', label: "Name(fr)", isOrderable: true},
          {id: 'order', label: "Order", isOrderable: true}
        ];
      case "reasonCodesetName":
        return [
          {id: 'id', label: "Id", isOrderable: true},
          {id: 'name_en', label: "Name(en)", isOrderable: true},
          {id: 'name_fr', label: "Name(fr)", isOrderable: true}
        ];
      default:
        return [];
    }
  }

  const handleChange=(value)=>{
    setBackdropOpen(true);
    let promise = getRowsPromise(value);

    promise.then((data)=>{
      setCodesetData({
        codeset: value,
        rows: data.data,
        headCells: getHeadCells(value)
      });
    })
    .finally(()=>{
      setBackdropOpen(false);
    });
  }

  const getTableRow=(row)=>{
    switch(codesetData.codeset){
      case "languageCodeset":
        return (
          <TableRow key={row.id}>
            <TableCell component="th" scope="row">{row.id}</TableCell>
            <TableCell>{row.code}</TableCell>
          </TableRow>
        );
      case "roleCodeset":
        return (
          <TableRow key={row.id}>
            <TableCell component="th" scope="row">{row.id}</TableCell>
            <TableCell>{row.name}</TableCell>
          </TableRow>
        );
      case "reasonCodeset":
        return (
          <TableRow key={row.id}>
            <TableCell component="th" scope="row">{row.id}</TableCell>
            <TableCell>{row.codeset_id}</TableCell>
            <TableCell>{row.name_en}</TableCell>
            <TableCell>{row.name_fr}</TableCell>
            <TableCell>{row.order}</TableCell>
          </TableRow>
        );
      case "requestStatusCodeset":
      case "userProjectActionCodeset":
      case "reasonCodesetName":
        return (
          <TableRow key={row.id}>
            <TableCell component="th" scope="row">{row.id}</TableCell>
            <TableCell>{row.name_en}</TableCell>
            <TableCell>{row.name_fr}</TableCell>
          </TableRow>
        );
      default:
        return <Root></Root>;
    }
  }

  return (
    <>
      <AppBar
        className={classes.appBarHeader}
        elevation={0}
      >
        <Typography
          variant="h6"
          component="h1"
        >
          View codesets
        </Typography>
      </AppBar>
      <Autocomplete
        id="codeset-select"
        className={classes.select}
        style={{marginTop: 8, marginBottom:4}}
        options={Object.keys(codesets)}
        getOptionLabel={(option)=>codesets[option]}
        renderInput={(params) => (
          <TextField
            {...params}
            multiline
            rows="1"
            label="Codeset"
            variant="outlined"
          />
        )}
        value={codesetData.codeset}
        onChange={(event, newValue) => {
          handleChange(newValue);
        }}
        disableClearable={true}
      />
      {codesetData.codeset !== null &&
        <EnhancedTable
          headCells={codesetData.headCells}
          rows={codesetData.rows}
          getTableRow={getTableRow}
        />
      }
      <Backdrop className={classes.backdrop} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar 
        message={snackbarState.message}
        severity={snackbarState.severity}
        open={snackbarState.open}
        handleClose={()=>{setSnackbarState({...snackbarState, open: false, message: ""})}}
      />
    </>
  );
}